import React from 'react';
import styled from 'styled-components';

import {
    MEMBER_STATE_STYLE,
    NAME_COL_WIDTH,
    STATE_COL_WIDTH,
} from './constants';

/**
 * 員工端與店長端共用的長條月表：人員為列、日期為欄。
 *
 * 桌機優先的兩個硬需求（驗收 11）：
 *   1. 姓名欄與狀態欄 sticky 在左邊，橫向捲動時不會捲走
 *   2. 日期欄固定寬度且不換行，文字不互蓋
 * 所以外層是 overflow-x:auto，table 用 width:max-content 而不是 100%。
 */
const Scroller = styled.div`
    width: 100%;
    overflow-x: auto;
    background-color: #fff;
`;

const Table = styled.table`
    width: max-content;
    min-width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 12px;

    th,
    td {
        border-right: 1px solid #eceff1;
        border-bottom: 1px solid #eceff1;
        text-align: center;
        vertical-align: middle;
        white-space: nowrap;
    }

    thead th {
        position: sticky;
        top: 0;
        z-index: 20;
        background-color: #f8f9fa;
        color: #4b5563;
        font-weight: bold;
        padding: 6px 2px;
        height: 60px;
        border-bottom: 1px solid #e2e6ea;
    }

    .col-name,
    .col-state {
        position: sticky;
        z-index: 30;
        background-color: #f8f9fa;
        box-shadow: none;
    }

    .col-name {
        left: 0;
        width: ${NAME_COL_WIDTH}px;
        min-width: ${NAME_COL_WIDTH}px;
        text-align: left;
        padding-left: 10px;
    }

    .col-state {
        left: ${NAME_COL_WIDTH}px;
        width: ${STATE_COL_WIDTH}px;
        min-width: ${STATE_COL_WIDTH}px;
        border-right: 1px solid #cfd6dd;
        box-shadow: 2px 0 0 rgba(0, 0, 0, 0.02);
    }

    tbody .col-name,
    tbody .col-state {
        z-index: 10;
        background-color: #fff;
    }

    tbody tr.is-self .col-name,
    tbody tr.is-self .col-state {
        background-color: #e7f1fb;
    }

    .day-col {
        width: 46px;
        min-width: 46px;
        padding: 0;
    }

    .weekday {
        font-size: 10px;
        color: #78909c;
        line-height: 1.2;
    }

    .day-num {
        font-size: 13px;
        font-weight: bold;
        line-height: 1.2;
    }

    .is-weekend {
        background-color: #fafafa;
    }

    thead th.is-weekend {
        background-color: #eef1f4;
    }

    thead th.is-today .day-num,
    thead th.is-today .weekday {
        color: #1864ab;
    }

    /* 日期欄頭可點＝開「當日明細」（唯讀）。⛔ 只有表頭吃這個點擊，
       格子的左鍵劃假／右鍵選單完全不受影響 —— 兩者是不同的 DOM 節點。 */
    thead th.is-day-clickable {
        cursor: pointer;
    }

    thead th.is-day-clickable:hover,
    thead th.is-day-clickable.is-day-open {
        background-color: #e7f1fb;
    }

    /* 展開中的那一欄：用 outline 標示而不是 box-shadow ——
       ⚠️ 人力不足的橘線也是 box-shadow，兩個一起套會互相蓋掉。 */
    thead th.is-day-clickable.is-day-open {
        outline: 2px solid #1864ab;
        outline-offset: -2px;
    }

    /* 人力可能不足的日期：欄底加橘線。⚠️ 只是標示，員工照樣劃得下去 */
    thead th.has-staffing-issue {
        box-shadow: inset 0 -3px 0 #e8590c;
        color: #d9480f;
    }

    /* 開診標示：只有雙診才亮藍色徽章，單診／休診不製造干擾。
       ⚠️ 固定高度 14px，⛔ 不可以只在部分日期出現就把表頭高度撐得高低不一。 */
    .clinic-tag {
        display: block;
        height: 14px;
        margin-top: 1px;
        font-size: 9px;
        line-height: 14px;
        font-weight: bold;
        color: transparent;
    }

    .clinic-tag.is-dual {
        color: #1864ab;
        background-color: #e7f1fb;
        border-radius: 7px;
    }

    /* ⛔ 未設定不可以當成單診：留一個灰問號說明「還沒設定」，但不用警示色嚇人 */
    .clinic-tag.is-unset {
        color: #b0bec5;
        font-weight: normal;
    }

    /* ---- 每日人力統計列（tfoot）----
       跟班表同一張 <table>，所以 1–31 欄永遠對齊、共用同一條水平捲軸。
       ⛔ 不可以拆成第二張表用 scroll 事件同步 —— 那一定會在某個縮放比例下錯位。 */
    tfoot th,
    tfoot td {
        border-top: 1px solid #cfd6dd;
        border-right: 1px solid #dde3e8;
        background-color: #f8f9fa;
    }

    /* 列標籤橫跨姓名＋狀態兩欄：統計列不需要「狀態」，⛔ 也不該為了填滿它塞說明文字。
       sticky 位移量必須等於兩欄寬度相加，否則橫向捲動時會露出表身。 */
    tfoot th.load-head {
        position: sticky;
        left: 0;
        z-index: 30;
        width: ${NAME_COL_WIDTH + STATE_COL_WIDTH}px;
        min-width: ${NAME_COL_WIDTH + STATE_COL_WIDTH}px;
        text-align: left;
        padding-left: 10px;
        background-color: #eef1f4;
        font-weight: bold;
        border-right: 1px solid #cfd6dd;
    }

    /* 職能列：呈現完全沿用「4 排班預覽」的職能列 —— 上下午相同只出現一個大字
       （.count-one），不同時才拆兩行並一定帶「上／下」標籤（.count-line）。
       ⛔ 不自創格式：兩頁是同一份數字，看起來也必須是同一種寫法。 */
    tfoot .count-cell {
        height: 34px;
        padding: 1px 0;
    }

    tfoot .count-one {
        display: block;
        font-size: 13px;
        font-weight: bold;
        line-height: 32px;
        color: #37474f;
    }

    tfoot .count-line {
        display: block;
        font-size: 10px;
        line-height: 1.25;
        white-space: nowrap;
        color: #37474f;

        i {
            font-style: normal;
            color: #78909c;
            margin-right: 1px;
        }
    }

    /* 缺額只落在單一時段時，只有那一行變紅 —— ⛔ 不可以因為合併顯示就把半天缺額蓋掉 */
    tfoot .count-line.is-short,
    tfoot .count-one.is-short {
        color: #d9480f;
        font-weight: bold;
    }

    /* 有缺額的那一格：沿用預覽頁（步驟 4）標缺額的同一組顏色，⛔ 不自創新的視覺語言 */
    tfoot .count-short {
        background-color: #fff4e6;
        outline: 1px solid #f0a760;
        outline-offset: -1px;
    }

    tfoot .count-one.is-closed {
        color: #b0bec5;
        font-size: 11px;
        font-weight: normal;
    }
`;

const NameCell = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;

    .self-tag {
        font-size: 10px;
        background-color: #1864ab;
        color: #fff;
        border-radius: 8px;
        padding: 1px 6px;
    }

    .name {
        font-weight: bold;
    }
`;

/* 姓名底下的「職能軌道 · 第 N 順位」。⛔ 只是說明文字，⛔ 不影響任何格子的可點性 */
const RankLine = styled.div`
    font-size: 10.5px;
    color: #7b8794;
    line-height: 1.4;
    margin-top: 1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const StateBadge = styled.span`
    display: inline-block;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    line-height: 1.5;
    background-color: ${(props) => props.styles.background};
    color: ${(props) => props.styles.color};
    border: 1px solid ${(props) => props.styles.border};
`;

const MarkCell = styled.td`
    height: 40px;
    cursor: ${(props) => (props.clickable ? 'pointer' : 'default')};
    background-color: ${(props) => (props.isWeekend ? '#fafafa' : 'transparent')};
    position: relative;

    &:hover {
        outline: ${(props) => (props.clickable ? '2px solid #1864ab' : 'none')};
        outline-offset: -2px;
    }
`;

/* 協調狀態的角標。⛔ 只是標示，⛔ 不改變任何格子的可點性與既有行為。 */
const CoordDot = styled.span`
    position: absolute;
    top: 2px;
    right: 2px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: ${(props) => props.tone};
`;

const MarkChip = styled.div`
    /* 待協調＝還沒定案：虛線框；維持上班＝店長已否決：灰底加刪除線。
       ⛔ 兩者都不刪掉員工劃的假，只是把「現在的下場」畫出來。 */
    &.is-coord-pending {
        outline: 2px dashed #f76707;
        outline-offset: 1px;
    }

    &.is-coord-kept_work {
        background-color: #ced4da !important;
        text-decoration: line-through;
    }

    display: inline-block;
    min-width: 34px;
    padding: 2px 3px;
    border-radius: 4px;
    font-size: 10px;
    line-height: 1.3;
    color: #fff;
    background-color: ${(props) => props.color};

    .period {
        display: block;
        font-size: 9px;
        opacity: 0.9;
    }
`;

/**
 * 店長端的正式日別（例假日／休息日／具名假別）。
 *
 * ⛔ 分色 ⛔ 不可以是唯一辨識 —— 每一格都有短文字（例／息／假）＋ 完整 tooltip；
 *    上班格 ⛔ 完全不顯示文字（⛔ 不出現「希休／休」兩套字樣）。
 * ⚠️ 店長手動調過的那一格用實心外框標示，⛔ 不是只改顏色深淺。
 */
const DayTypeChip = styled.div`
    display: inline-block;
    min-width: 30px;
    padding: 2px 4px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: bold;
    line-height: 1.35;
    color: #fff;
    background-color: ${(props) => props.color};

    &.is-manual {
        outline: 2px solid #1f2933;
        outline-offset: 1px;
    }

    .dt-half {
        display: block;
        font-size: 9px;
        font-weight: normal;
        opacity: 0.9;
    }
`;

/* 「待人資人工處理」的角標。⛔ 只是標記，⛔ 不改變日別、也不代表系統做了任何補休 */
const HrDot = styled.span`
    position: absolute;
    bottom: 2px;
    right: 2px;
    font-size: 8px;
    font-weight: bold;
    line-height: 1;
    color: #8a5200;
`;

const EmptyHint = styled.span`
    color: #d4dae0;
    font-size: 11px;
`;

/**
 * 日期表頭的開診標示。⚠️ 醫師以整日計算，am/pm 一律同值 → 只出一個徽章：
 *   雙診 → 雙　／　還沒設定 → 灰色「?」（⛔ 不可以把未知當成單診）
 * 其餘（單診／休診）不顯示，避免整排都是標籤反而看不到重點。
 * ⚠️ 舊資料若上下午不同，退回「雙上・雙下」如實標示，⛔ 不合併成猜出來的整日值。
 */
const clinicTag = (clinic, labels) => {
    if (!clinic) return { text: '', className: '', title: '開診資料尚未設定' };

    const label = (mode) => labels[mode] || '未設定';
    const same = clinic.am === clinic.pm;
    const title = same
        ? `當日：${label(clinic.am)}`
        : `舊資料 上午：${label(clinic.am)}／下午：${label(clinic.pm)}`;
    const amDual = clinic.am === 'dual';
    const pmDual = clinic.pm === 'dual';

    if (amDual && pmDual) return { text: '雙', className: 'is-dual', title: `雙診（${title}）` };
    if (amDual) return { text: '雙上', className: 'is-dual', title: `上午雙診（${title}）` };
    if (pmDual) return { text: '雙下', className: 'is-dual', title: `下午雙診（${title}）` };

    if (clinic.am === 'unset' || clinic.pm === 'unset') {
        return { text: '?', className: 'is-unset', title: `開診資料尚未設定（${title}）` };
    }

    return { text: '', className: '', title };
};

const SESSIONS = ['am', 'pm'];
const SESSION_SHORT = { am: '上', pm: '下' };

/** 一個時段的顯示字串：有需求就寫「在班/需求」，沒有就只寫在班人數（沿用「4 排班預覽」） */
const countText = (cell) => (cell.need != null ? `${cell.actual}/${cell.need}` : `${cell.actual}`);

/**
 * 統計列的一格。`skillId === null` ＝在班總人數列，否則是該職能列。
 *
 * ⚠️ 數字與呈現方式都照抄「4 排班預覽」的職能列（同一支 StaffingEvaluator、同一個
 *    countText 寫法），⛔ 這裡不做任何加總或判定以外的推算，也不自己決定「幾個人才夠」。
 * ⚠️ 總人數沒有需求值（requirementMap 只給職能層級的需求），所以總人數列不會出現「／需求」。
 */
const countCell = (date, skillId, rowLabel, sessionLabels, counts, required, clinic) => {
    const all = SESSIONS.map((session) => {
        const cell = (counts[date] || {})[session] || {};
        const actual =
            skillId === null ? cell.total_on_duty || 0 : (cell.on_duty || {})[skillId] || 0;
        const want = skillId === null ? null : ((required[date] || {})[session] || {})[skillId];

        return {
            session,
            actual,
            need: want == null ? null : want,
            closed: (clinic || {})[session] === 'none',
            short: want != null && actual < want,
        };
    });

    // 休診的時段沒有在班語意，⛔ 不製造不存在的另一筆。兩個時段都休診時仍只顯示一格。
    const open = all.filter((c) => !c.closed);
    const cells = open.length ? open : [all[0]];
    const merged =
        open.length === 0 ||
        (cells.length === 2 && cells[0].actual === cells[1].actual && cells[0].need === cells[1].need);
    const short = cells.some((c) => c.short);
    const title = all
        .map((c) =>
            c.closed
                ? `${date} ${sessionLabels[c.session]} 休診`
                : `${date} ${merged ? '全天' : sessionLabels[c.session]}${rowLabel} ${c.actual} 人${
                      c.need != null
                          ? `／需求 ${c.need} 人${c.short ? `（缺 ${c.need - c.actual} 人）` : ''}`
                          : ''
                  }`
        )
        .filter((text, i) => !(merged && i > 0))
        .join('　');

    return { allClosed: open.length === 0, cells, merged, short, title };
};

/** 這一列要不要印順位：'all'＝全員（店長端）、'self'＝只有自己（員工端）、其他＝都不印 */
const showRank = (member, scope) => {
    if (!member || !member.rank) return false;
    if (scope === 'all') return true;

    return scope === 'self' && !!member.is_self;
};

const rankTitle = (member) => {
    const r = member.rank || {};
    const base = r.skill_name ? `本月歸軌職能：${r.skill_name}` : '本月主要職能尚未指定，順位無從判定';

    return r.rank ? `${base}\n軌道內共 ${r.total} 人　·　跨軌道的順位數字不能互相比較` : base;
};

/**
 * 一格的協調狀態要畫什麼。⛔ 只有店長端（showCoordination）才會拿到這份資料，
 * 員工劃假頁的格子行為與外觀完全不受影響。
 */
const COORD_TONE = {
    auto: '#2f9e44',
    pending: '#f76707',
    agreed: '#2f9e44',
    kept_work: '#adb5bd',
};

const CoordinationTable = ({
    payload,
    onCellClick,
    onCellContextMenu,
    onDayHeaderClick,
    activeCellKey,
    activeDate,
    showStaffing,
    rankScope,
    showCoordination,
    showDayTypes,
}) => {
    const { dates = [], members = [] } = payload || {};
    // 人力提示：哪些日期可能有缺額。⚠️ 只用來標示與提供 tooltip，⛔ 不影響能不能劃假。
    // ⛔ 這裡刻意不標「待補資料」（開診表未設定）—— 那是店長要補的東西，
    //    標在員工端只會讓整排日期都變橘色，把真正的人力缺額淹掉。
    const staffingByDate = ((payload || {}).staffing || {}).by_date || {};
    // 開診（單診／雙診）也走同一份 staffing payload，⛔ 不另外打一支 API，
    // 也 ⛔ 不要求員工進管理者的「本月設定」頁才看得到。
    const clinicByDate = ((payload || {}).staffing || {}).clinic || {};
    const clinicLabels = ((payload || {}).staffing || {}).clinic_labels || {};
    // 每日人力統計列（店長端彙整頁才開）：職能、在班人數、需求人數全部由後端一次給齊。
    // ⚠️ 讀的是「4 排班預覽」同一份計算，⛔ 兩頁不會算出不一樣的數字。
    const staffing = (payload || {}).staffing || {};
    const loadSkills = staffing.skills || [];
    const loadCounts = staffing.counts || {};
    const loadRequired = staffing.required || {};
    const sessionLabels = staffing.session_labels || { am: '上午', pm: '下午' };
    const withStaffing = !!showStaffing && loadSkills.length > 0;
    // 待協調排休：uuid|date => {state,label,reasons}。⛔ 員工端沒有這份資料。
    const coordByCell = showCoordination ? ((payload || {}).coordination || {}).by_cell || {} : {};
    // 正式日別（例假日／休息日／具名假別）。⛔ 只有店長端拿得到也只有店長端畫得出來 ——
    // 員工端 showDayTypes 永遠是 undefined，格子的外觀與行為完全不變。
    const dayTypePlan = showDayTypes ? (payload || {}).day_types || {} : {};
    const dayTypeByCell = dayTypePlan.by_cell || {};
    const hasDayTypePlan = !!dayTypePlan.has_plan;

    return (
        <Scroller>
            <Table>
                <thead>
                    <tr>
                        <th className="col-name">姓名</th>
                        <th className="col-state">狀態</th>
                        {dates.map((d) => {
                            const raw = staffingByDate[d.date];
                            const issue = raw && (raw.hard > 0 || raw.soft > 0) ? raw : null;
                            const clinic = clinicTag(clinicByDate[d.date], clinicLabels);

                            const dayClickable = !!onDayHeaderClick;
                            const baseTitle = issue
                                ? `${d.date}　${clinic.title}　${(issue.messages || [])
                                      .filter((m) => m.indexOf('待補資料') < 0)
                                      .join('\n')}`
                                : `${d.date}　${clinic.title}`;

                            return (
                                <th
                                    key={d.date}
                                    data-day-head={d.date}
                                    className={`day-col${d.is_weekend ? ' is-weekend' : ''}${
                                        d.is_today ? ' is-today' : ''
                                    }${issue ? ' has-staffing-issue' : ''}${
                                        dayClickable ? ' is-day-clickable' : ''
                                    }${activeDate === d.date ? ' is-day-open' : ''}`}
                                    title={
                                        dayClickable ? `${baseTitle}\n點一下看這天誰想休（只看不改）` : baseTitle
                                    }
                                    role={dayClickable ? 'button' : undefined}
                                    tabIndex={dayClickable ? 0 : undefined}
                                    aria-label={dayClickable ? `${d.date} 當日明細` : undefined}
                                    onClick={dayClickable ? () => onDayHeaderClick(d) : undefined}
                                    onKeyDown={
                                        dayClickable
                                            ? (e) => {
                                                  if (e.key === 'Enter' || e.key === ' ') {
                                                      e.preventDefault();
                                                      onDayHeaderClick(d);
                                                  }
                                              }
                                            : undefined
                                    }
                                >
                                    <div className="weekday">{d.weekday}</div>
                                    <div className="day-num">{d.day}</div>
                                    <span
                                        className={`clinic-tag ${clinic.className}`}
                                        data-clinic={`${d.date}`}
                                    >
                                        {clinic.text || '\u00a0'}
                                    </span>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {members.length === 0 && (
                        <tr>
                            <td colSpan={dates.length + 2} style={{ padding: '24px', color: '#90a4ae' }}>
                                沒有可顯示的人員
                            </td>
                        </tr>
                    )}
                    {members.map((member) => {
                        const marksByDate = {};
                        (member.marks || []).forEach((mark) => {
                            marksByDate[mark.date] = mark;
                        });
                        const stateStyle = MEMBER_STATE_STYLE[member.state] || MEMBER_STATE_STYLE.pending;

                        return (
                            <tr key={member.uuid} className={member.is_self ? 'is-self' : ''}>
                                <td className="col-name">
                                    <NameCell>
                                        <span className="name">{member.name}</span>
                                        {member.is_self && <span className="self-tag">我</span>}
                                    </NameCell>
                                    {/* 員工端只印自己那一條軌道的順位（跨軌道的數字不能互相比較，
                                        印出來只會讓人拿不同軌道的 1 和 2 互相對照）；店長端要取捨，所以全員都印。 */}
                                    {showRank(member, rankScope) && (
                                        <RankLine title={rankTitle(member)}>{member.rank.label}</RankLine>
                                    )}
                                </td>
                                <td className="col-state" data-state-of={member.uuid}>
                                    <StateBadge styles={stateStyle} title={member.last_change_summary || ''}>
                                        {member.state_label}
                                    </StateBadge>
                                </td>
                                {dates.map((d) => {
                                    const mark = marksByDate[d.date];
                                    const cellKey = `${member.uuid}|${d.date}`;
                                    const coord = coordByCell[cellKey];
                                    const dayType = dayTypeByCell[cellKey];
                                    // 店長端：有日別分類就整格可點（開明細／覆寫）；
                                    // 員工端維持原本的「只有自己的格子可點」，⛔ 行為完全不變。
                                    const clickable = showDayTypes
                                        ? hasDayTypePlan && !!onCellClick
                                        : !!member.can_edit && !!onCellClick;
                                    // 右鍵在「所有」格子上都要接：自己的格子開精簡選單，同事的格子
                                    // 只擋掉瀏覽器原生選單並提示唯讀（⛔ 不改任何資料）。
                                    // 店長端沒傳 onCellContextMenu ⇒ 右鍵行為完全不變。
                                    return (
                                        <MarkCell
                                            key={cellKey}
                                            data-cell={cellKey}
                                            className={`day-col${d.is_weekend ? ' is-weekend' : ''}`}
                                            clickable={clickable}
                                            isWeekend={d.is_weekend}
                                            title={
                                                [
                                                    dayType
                                                        ? `${member.name} ${d.date} ${dayType.label}${
                                                              dayType.is_manual ? '（店長調整）' : '（系統建議）'
                                                          }`
                                                        : mark
                                                        ? `${member.name} ${d.date} ${mark.category_label}・${mark.period_label}`
                                                        : `${member.name} ${d.date} 上班`,
                                                    dayType ? dayType.effect : null,
                                                    dayType && dayType.reason ? dayType.reason : null,
                                                    coord ? coord.label : null,
                                                    coord && coord.reasons && coord.reasons.length
                                                        ? coord.reasons.join('\n')
                                                        : null,
                                                ]
                                                    .filter(Boolean)
                                                    .join('\n')
                                            }
                                            style={
                                                activeCellKey === cellKey
                                                    ? { outline: '2px solid #1864ab', outlineOffset: '-2px' }
                                                    : undefined
                                            }
                                            onClick={clickable ? () => onCellClick(member, d) : undefined}
                                            onContextMenu={
                                                onCellContextMenu ? (e) => onCellContextMenu(member, d, e) : undefined
                                            }
                                        >
                                            {/* 有日別分類時，格子顯示的就是**正式日別**，
                                                ⛔ 不再同時出現員工那一層的「一般休／特休」字樣；
                                                上班格 ⛔ 完全不顯示文字。 */}
                                            {hasDayTypePlan ? (
                                                dayType && dayType.day_type !== 'work' ? (
                                                    <DayTypeChip
                                                        color={dayType.color}
                                                        className={dayType.is_manual ? 'is-manual' : undefined}
                                                        data-day-type={dayType.day_type}
                                                    >
                                                        {dayType.short}
                                                        {dayType.half_day !== 'full' && (
                                                            <span className="dt-half">
                                                                {dayType.half_day === 'am' ? '上' : '下'}
                                                            </span>
                                                        )}
                                                    </DayTypeChip>
                                                ) : mark && coord ? (
                                                    /* 還沒協調完成（待協調／維持上班）的排休不會有日別，
                                                       但 ⛔ 不可以因此從表上消失 —— 店長就是要在這裡處理它。 */
                                                    <MarkChip
                                                        color={mark.color}
                                                        className={`is-coord-${coord.state}`}
                                                    >
                                                        {mark.category_label}
                                                        {mark.period !== 'full' && (
                                                            <span className="period">{mark.period_label}</span>
                                                        )}
                                                    </MarkChip>
                                                ) : null
                                            ) : mark ? (
                                                <MarkChip
                                                    color={mark.color}
                                                    className={coord ? `is-coord-${coord.state}` : undefined}
                                                >
                                                    {mark.category_label}
                                                    {mark.period !== 'full' && (
                                                        <span className="period">{mark.period_label}</span>
                                                    )}
                                                </MarkChip>
                                            ) : (
                                                <EmptyHint>上班</EmptyHint>
                                            )}
                                            {coord && (
                                                <CoordDot
                                                    tone={COORD_TONE[coord.state] || '#adb5bd'}
                                                    data-coord-state={coord.state}
                                                />
                                            )}
                                            {dayType && dayType.hr_review && <HrDot title="待人資人工處理">人資</HrDot>}
                                        </MarkCell>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
                {withStaffing && (
                    /* 每日人力統計列：在班總人數 ＋ **每個職能各一列**（職能清單由後端動態給，
                       ⛔ 不寫死）。跟班表同一張 <table> ⇒ 1–31 欄永遠對齊、共用同一條水平捲軸。 */
                    <tfoot>
                        {[{ id: null, name: '在班總人數', label: '在班' }]
                            .concat(loadSkills.map((s) => ({ id: s.id, name: `${s.name} 在班`, label: '在班' })))
                            .map((row) => (
                                <tr key={row.id === null ? 'total' : row.id}>
                                    <th className="load-head" colSpan={2}>
                                        {row.name}
                                    </th>
                                    {dates.map((d) => {
                                        const info = countCell(
                                            d.date,
                                            row.id,
                                            row.label,
                                            sessionLabels,
                                            loadCounts,
                                            loadRequired,
                                            clinicByDate[d.date]
                                        );
                                        const closedTotal = info.allClosed && row.id === null;

                                        return (
                                            <td
                                                key={`${row.id}|${d.date}`}
                                                data-load={`${row.id === null ? 'total' : row.id}|${d.date}`}
                                                className={`day-col count-cell${
                                                    info.short ? ' count-short' : ''
                                                }`}
                                                title={info.title}
                                            >
                                                {closedTotal ? (
                                                    <span className="count-one is-closed">休</span>
                                                ) : info.merged ? (
                                                    <span
                                                        className={`count-one${
                                                            info.cells[0].short ? ' is-short' : ''
                                                        }`}
                                                    >
                                                        {countText(info.cells[0])}
                                                    </span>
                                                ) : (
                                                    info.cells.map((c) => (
                                                        <span
                                                            className={`count-line${c.short ? ' is-short' : ''}`}
                                                            key={c.session}
                                                        >
                                                            <i>{SESSION_SHORT[c.session]}</i>
                                                            {countText(c)}
                                                        </span>
                                                    ))
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                    </tfoot>
                )}
            </Table>
        </Scroller>
    );
};

export default CoordinationTable;
